import { TerritoryFallbackColor, territoryPaletteColor } from '../../../map/features/territories/territory-colors';
import * as L from 'leaflet';
import {
  GapTolerancePx, SnapRadiusPx, absorbGaps, conflateRing, pullOutOfOverlaps, snapPoint,
} from '../../../map/features/territories/boundary-snapping';
import {
  FreehandMaxVertices, FreehandToleranceMeters, dropRepeats, simplifyRingWithinBudget,
} from './boundary-simplify';
import { validateRings } from './boundary-validation';

/**
 * Owns boundary drawing on the persistent workspace map, and only while a draw
 * is in progress.
 *
 * The gestures it takes over — press-drag-release — belong to the shared map,
 * so every one of them is bound on
 * attach and unbound on detach. Detach never touches the map itself: the base
 * layer, the view, and every other feature outlive the draw.
 *
 * Drawing is hand-rolled rather than a draw plugin: the interaction is small
 * enough to own, and it keeps the bundle free of another map dependency.
 *
 * ONE GESTURE: press, drag the shape you mean, let go, and it closes. The rough
 * trace is then simplified (see boundary-simplify.ts) so what lands in the editor
 * is a ring with corners you can actually take hold of rather than the raw
 * pointer trail. Placing corners one click at a time used to sit beside it and
 * was removed: sketching and then dragging the handles the editor puts on every
 * corner reaches the same shape, and two drawing modes on one map meant two sets
 * of gestures to bind, unbind and keep from outliving each other.
 *
 * The tool's job ends at handing over one closed ring. Moving corners, adding
 * them, deleting them and keeping the ring valid is TerritoryBoundaryEditor's,
 * which the page opens on the new patch the moment it is staged.
 *
 * SEAMS ARE THE POINT. Patches may not overlap, and two sketched freehand never
 * share an edge on their own - they cross by a metre somewhere, and every save
 * then reports a repair and moves somebody's border. So the trace is not merely
 * snapped point by point; wherever it runs along a neighbour, that neighbour's
 * own vertices are adopted verbatim (`conflateRing`), which is what makes the
 * shared edge identical on both sides instead of nearly so. Snapping is shown
 * while it happens, and held off entirely while Alt is down, because a boundary
 * that genuinely runs a few metres inside a neighbour has to remain drawable.
 *
 * Proximity is only half of it. A corner sketched WELL INSIDE a neighbour is
 * near none of its edges, so nothing pulls it and the two patches simply
 * overlap. Those corners are brought back out onto the border they crossed
 * (`pullOutOfOverlaps`) before anything else runs, which both removes the
 * overlap and puts them where conflation can then share whole edges.
 */
export class TerritoryDrawController {
  /** Vertices placed so far, in order. */
  points: L.LatLng[] = [];

  /**
   * Why the ring was refused, or null. Set instead of finishing, so a shape that
   * cannot be saved stays on the map to be fixed rather than vanishing with the
   * work that made it.
   */
  problem: string | null = null;

  private readonly draftLayer = L.layerGroup();
  private readonly onTraceDown = (e: L.LeafletMouseEvent) => this.beginTrace(e);
  private readonly onTraceMove = (e: L.LeafletMouseEvent) => this.extendTrace(e);
  private readonly onTraceUp = () => this.endTrace();
  private readonly onDocumentUp = (event: MouseEvent) => {
    if (event.button === 0) this.endTrace();
  };
  private readonly onTraceBlur = () => {
    if (!this.tracing) return;
    this.releaseTrace();
    this.trace = [];
    this.liveSnap = null;
    this.liveEdge = null;
    this.snapHeldOff = false;
    this.renderDraft();
    this.onChange();
  };
  private attached = false;
  private color = TerritoryFallbackColor;
  private snapTargets: readonly (readonly L.LatLng[])[] = [];
  /** True between pressing and releasing during a freehand sketch. */
  private tracing = false;
  /** Raw pointer samples of the sketch in progress, before simplification. */
  private trace: L.LatLng[] = [];
  /**
   * Where the pointer would be taken to right now, and the neighbouring edge it
   * would be taken to. Drawn while tracing, so that a seam forming is visible as
   * it forms rather than discovered at save time.
   */
  private liveSnap: L.LatLng | null = null;
  private liveEdge: readonly [L.LatLng, L.LatLng] | null = null;
  /** True while Alt is held: snapping is off for as long as it is. */
  private snapHeldOff = false;
  /** How the last released sketch seamed onto its neighbours. */
  private lastAdopted = 0;
  private lastSeams = 0;
  /** How many of its corners had to be brought back out of one. */
  private lastPulled = 0;
  /** How many stopped short of one, leaving a gap that was absorbed. */
  private lastClosed = 0;

  /**
   * @param map      the shared workspace map
   * @param onFinish called with the closed ring's GeoJSON
   * @param onChange called whenever the point count changes, so the page can
   *                 re-render its buttons without polling
   */
  constructor(
    private readonly map: L.Map,
    private readonly onFinish: (geoJson: string) => void,
    private readonly onChange: () => void,
  ) {}

  get active(): boolean { return this.attached; }

  /** True while the pointer is down and the sketch is being traced. */
  get sketching(): boolean { return this.tracing; }

  /** True while the trace is currently running along a neighbour's boundary. */
  get snapping(): boolean { return this.liveSnap !== null; }

  /** True while Alt is held down and snapping is deliberately off. */
  get snapSuspended(): boolean { return this.snapHeldOff; }

  /** Corners the last released sketch took from its neighbours, and how many
   *  separate runs they came in. Zero adopted means it shares no edge with
   *  anything - worth saying, because that is when a repair can still fire. */
  get seamStats(): { adopted: number; seams: number; pulled: number; closed: number } {
    return {
      adopted: this.lastAdopted, seams: this.lastSeams,
      pulled: this.lastPulled, closed: this.lastClosed,
    };
  }

  /** A ring needs three distinct corners before it encloses anything. */
  get canFinish(): boolean { return this.attached && this.points.length >= 3; }

  /**
   * The shape on screen: the corners placed so far, or the sketch being traced.
   * The page measures its area from this, so the readout answers "is this patch
   * the right size" during a freehand drag too, not only after it.
   */
  get outline(): readonly L.LatLng[] {
    return this.points.length ? this.points : this.trace;
  }

  start(
    color: string,
    snapTargets: readonly (readonly L.LatLng[])[] = [],
  ): void {
    // Detached first: a restart has to give back the gestures the previous one
    // took, or two live handlers both answer the same press.
    if (this.attached) this.detach();
    this.color = territoryPaletteColor(color);
    this.points = [];
    this.problem = null;
    this.trace = [];
    this.liveSnap = null;
    this.liveEdge = null;
    this.snapHeldOff = false;
    this.lastAdopted = 0;
    this.lastSeams = 0;
    this.lastPulled = 0;
    this.lastClosed = 0;
    this.snapTargets = snapTargets;
    this.attach();
    this.renderDraft();
    this.onChange();
  }

  private attach(): void {
    this.map.addLayer(this.draftLayer);
    this.map.on('mousedown', this.onTraceDown);
    this.map.on('mousemove', this.onTraceMove);
    this.map.on('mouseup', this.onTraceUp);
    this.attached = true;
  }

  /** Every gesture back. Unbinding a handler that was never bound is a no-op. */
  private detach(): void {
    if (!this.attached) return;
    this.releaseTrace();
    this.map.off('mousedown', this.onTraceDown);
    this.map.off('mousemove', this.onTraceMove);
    this.map.off('mouseup', this.onTraceUp);
    this.map.removeLayer(this.draftLayer);
    this.attached = false;
  }

  // ── Freehand ────────────────────────────────────────────────────────────

  /**
   * Pressing down starts a sketch. Map dragging goes off for its duration: the
   * whole gesture is press-drag-release, which is also how the map pans, and the
   * two cannot both have it. It comes straight back on release, including a
   * release that lands outside the shape or outside the map.
   */
  private beginTrace(e: L.LeafletMouseEvent): void {
    if (!this.attached || this.tracing) return;
    this.tracing = true;
    this.problem = null;
    this.points = [];
    this.lastAdopted = 0;
    this.lastSeams = 0;
    this.lastPulled = 0;
    this.lastClosed = 0;
    this.trace = [e.latlng];
    this.map.dragging.disable();
    document.addEventListener('mouseup', this.onDocumentUp, true);
    window.addEventListener('blur', this.onTraceBlur);
    this.track(e);
    this.renderDraft();
    this.onChange();
  }

  private extendTrace(e: L.LeafletMouseEvent): void {
    // Tracked even when the pointer is up, so the hint can say that a press
    // starting here would begin on a neighbour's line.
    this.track(e);
    if (!this.tracing) { this.renderDraft(); return; }

    const at = e.latlng;
    const last = this.trace[this.trace.length - 1];
    // A pointer reports many samples per pixel. Anything nearer than a couple of
    // pixels is tremor the simplifier would drop anyway, and drawing it costs a
    // frame per sample at exactly the moment the gesture needs to feel smooth.
    if (last && this.map.latLngToLayerPoint(at)
      .distanceTo(this.map.latLngToLayerPoint(last)) < 2) return;
    this.trace.push(at);
    this.renderDraft();
    this.onChange();
  }

  /**
   * Where the pointer is relative to the neighbours: whether it is near enough
   * to be taken, and which of their edges would take it. Reading Alt from the
   * event rather than a keyboard listener keeps the answer true even when the
   * key went down while the map did not have focus.
   */
  private track(e: L.LeafletMouseEvent): void {
    this.snapHeldOff = !!(e.originalEvent as MouseEvent | undefined)?.altKey;
    if (this.snapHeldOff) { this.liveSnap = null; this.liveEdge = null; return; }

    const found = snapPoint(e.latlng, this.map, this.snapTargets, SnapRadiusPx);
    this.liveSnap = found ? found.latlng : null;
    if (!found) { this.liveEdge = null; return; }

    // The neighbour's own segment under the cursor, so what is about to be
    // shared is highlighted rather than merely implied by a dot.
    const ring = this.snapTargets[found.ring];
    const i = Math.floor(found.at) % ring.length;
    this.liveEdge = [ring[i], ring[(i + 1) % ring.length]];
  }

  /**
   * Letting go closes the trace. It is simplified, then conflated against the
   * neighbours: every corner that landed near one is taken onto it, and wherever
   * the trace ran ALONG one, that neighbour's own vertices are adopted, so the
   * shared edge is identical on both sides instead of a metre away from it.
   *
   * With Alt held the trace is taken as drawn. A boundary that genuinely runs
   * just inside a neighbour is a real case, and a tool that cannot draw it is
   * worse than one that occasionally has to be told not to help.
   */
  private endTrace(): void {
    if (!this.tracing) return;
    this.releaseTrace();
    const traced = this.trace;
    const ignoreSnapping = this.snapHeldOff;
    this.trace = [];
    this.liveSnap = null;
    this.liveEdge = null;

    // A press that did not travel is a click, not a sketch. Nothing is drawn and
    // the tool stays armed for the real gesture.
    if (traced.length < 3) {
      this.renderDraft();
      this.onChange();
      return;
    }

    const { ring } = simplifyRingWithinBudget(
      traced, FreehandToleranceMeters, FreehandMaxVertices);

    if (ignoreSnapping) {
      this.points = dropRepeats(ring);
      this.lastAdopted = 0;
      this.lastSeams = 0;
      this.lastPulled = 0;
      this.lastClosed = 0;
    } else {
      // Corners that landed inside a neighbour come out first. Proximity has
      // nothing to say about them - they are near none of its edges - so
      // conflation alone would leave the patch sitting on top of its neighbour.
      // Once they are on the border, they are ON the border, which is precisely
      // what conflation then has something to work with.
      const pulled = pullOutOfOverlaps(ring, this.map, this.snapTargets);
      // Then the opposite miss: corners that stopped just short of a neighbour,
      // leaving a ribbon of ground in no territory at all. Absorbing it into the
      // shape being drawn is what the admin meant by running the line along that
      // border, and it is also what puts those corners ON the border - which is
      // what conflation needs before it can adopt a run.
      const closed = absorbGaps(pulled.ring, this.map, this.snapTargets);
      const seamed = conflateRing(closed.ring, this.map, this.snapTargets, GapTolerancePx);
      this.points = dropRepeats(seamed.ring);
      this.lastAdopted = seamed.adopted;
      this.lastSeams = seamed.seams;
      this.lastPulled = pulled.pulled;
      this.lastClosed = closed.closed;
    }

    this.renderDraft();
    this.onChange();
    this.finish();
  }

  /**
   * Close the ring and hand it over — unless it is not a boundary.
   *
   * A refusal keeps the shape on the map and says why. The alternative is
   * throwing away twenty clicks, or a whole sketch, for a fault the person can
   * see and fix. The rules themselves live in boundary-validation.ts, next to a
   * note about which server rule each one mirrors: the server still decides, this
   * only says so sooner.
   */
  finish(): void {
    if (!this.canFinish) return;
    const problem = validateRings([this.points]);
    if (problem) {
      this.problem = problem;
      this.renderDraft();
      this.onChange();
      return;
    }
    const geoJson = this.toGeoJson();
    this.cancel();
    this.onFinish(geoJson);
  }

  /** Stop listening as soon as the stroke finishes, is cancelled, or loses focus. */
  private releaseTrace(): void {
    document.removeEventListener('mouseup', this.onDocumentUp, true);
    window.removeEventListener('blur', this.onTraceBlur);
    if (this.tracing) this.map.dragging.enable();
    this.tracing = false;
  }

  /**
   * Give the map back. Safe to call when not drawing and safe to call twice —
   * a page tears down on route change without knowing whether a draw was live.
   */
  cancel(): void {
    this.points = [];
    this.trace = [];
    this.problem = null;
    this.liveSnap = null;
    this.liveEdge = null;
    this.snapHeldOff = false;
    this.draftLayer.clearLayers();
    this.detach();
    this.onChange();
  }

  /** GeoJSON ring: [lng, lat] order, first point repeated to close. */
  toGeoJson(): string {
    const ring = this.points.map(p => [p.lng, p.lat]);
    ring.push([this.points[0].lng, this.points[0].lat]);
    return JSON.stringify({ type: 'Polygon', coordinates: [ring] });
  }

  private renderDraft(): void {
    this.draftLayer.clearLayers();

    // The neighbour's edge the pointer is on, drawn under everything else. This
    // is the cue that a seam is being made: without it, "did it snap?" is only
    // answerable after saving, which is far too late to redraw the shape.
    if (this.liveEdge) {
      this.draftLayer.addLayer(L.polyline([...this.liveEdge], {
        color: '#2e7d32', weight: 6, opacity: 0.35, interactive: false,
      }));
    }

    // The sketch as it is being traced: an open line, because it is not a shape
    // until the pointer comes up and nothing should claim otherwise.
    if (this.tracing) {
      if (this.trace.length >= 2) {
        this.draftLayer.addLayer(L.polyline(this.trace, {
          color: this.color, weight: 3, opacity: 0.9, interactive: false,
        }));
      }
      // The line back to where the sketch began, so how much is left to close is
      // visible while there is still time to steer it.
      if (this.trace.length >= 3) {
        this.draftLayer.addLayer(L.polyline([this.trace[this.trace.length - 1], this.trace[0]], {
          color: this.color, weight: 1.5, dashArray: '2,6', opacity: 0.6, interactive: false,
        }));
      }
      this.renderSnapDot();
      return;
    }

    this.renderSnapDot();
    if (!this.points.length) return;

    // Show it closed once it is an area, so what you see is what saves. A refused
    // ring goes red: the thing that is wrong is the thing on the map.
    const stroke = this.problem ? '#c62828' : this.color;
    this.draftLayer.addLayer(this.points.length >= 3
      ? L.polygon(this.points, { color: stroke, weight: 2, dashArray: '5,5', fillOpacity: 0.15 })
      : L.polyline(this.points, { color: stroke, weight: 2, dashArray: '5,5' }));

    this.points.forEach(p => {
      this.draftLayer.addLayer(L.circleMarker(p, {
        radius: 4, color: '#fff', weight: 2, fillColor: stroke, fillOpacity: 1,
      }));
    });
  }

  /** Where the line is being pulled to, if it is. Green because that is the
   *  colour the editor already uses for a handle sitting on a neighbour. */
  private renderSnapDot(): void {
    if (!this.liveSnap) return;
    this.draftLayer.addLayer(L.circleMarker(this.liveSnap, {
      radius: 6, color: '#2e7d32', weight: 2,
      fillColor: '#fff', fillOpacity: 1, interactive: false,
    }));
  }
}
